/**
 * The strip of six squares under a saved estimate's picture: the user's view,
 * the four corner views and (twice as wide, with the 360 mark) the 360. Dark
 * and quiet while a picture is still to come, its thumbnail once it is in,
 * outlined when it is the one shown; a sweep runs over the one rendering.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PICTURE_ORDER, type PictureId, type RenderPicture } from './types';

export interface RenderGalleryProps {
  pictures: RenderPicture[];
  activeId: PictureId | 'preview';
  /** The picture Cycles is on, for the sweep. */
  renderingId: PictureId | null;
  onPick: (id: PictureId) => void;
}

/** The 360 mark: a ring with an arrow, as on cameras. */
const Icon360: React.FC = () => (
  <svg className="rgal-360" viewBox="0 0 48 24" aria-hidden="true" focusable="false">
    <ellipse cx="24" cy="12" rx="20" ry="8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeDasharray="90 14" strokeDashoffset="-6" />
    <path d="M40 4 L46 7 L41 11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <text x="24" y="15.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor" fontFamily="inherit">360</text>
  </svg>
);

export const RenderGallery: React.FC<RenderGalleryProps> = ({ pictures, activeId, renderingId, onPick }) => {
  const { t } = useTranslation('results');
  const byId = new Map(pictures.map((p) => [p.id, p]));
  return (
    <div className="rgal" role="group" aria-label={t('render.galleryLabel')}>
      {PICTURE_ORDER.map((id) => {
        const p = byId.get(id);
        const done = !!p?.done && !!p.url;
        const name = t(`render.pictures.${id}`);
        const cls = [
          'rgal-item',
          id === '360' ? 'rgal-item-360' : '',
          done ? 'is-done' : 'is-pending',
          activeId === id ? 'is-active' : '',
          renderingId === id && !done ? 'is-rendering' : '',
        ].filter(Boolean).join(' ');
        return (
          <button
            key={id}
            type="button"
            className={cls}
            disabled={!done}
            aria-pressed={activeId === id}
            aria-label={done ? t('render.show', { name }) : t('render.pending', { name })}
            title={done ? t('render.show', { name }) : t('render.pending', { name })}
            style={done && p?.thumbUrl ? { backgroundImage: `url("${p.thumbUrl}")` } : undefined}
            onClick={() => done && onPick(id)}
          >
            {id === '360' && <Icon360 />}
            <span className="rgal-sweep" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
};
